import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { gmbApi } from '@/services/gmbApi';

interface LocationOption {
  location_name: string;
  account_name: string;
  title: string | null;
  address: string | null;
  maps_uri: string | null;
}

interface GMBLocationPickerProps {
  darkMode: boolean;
  onSelected: () => void;
}

function getUserId() {
  return localStorage.getItem('user_id') || '';
}

/**
 * D-198: the connected Google login manages several Business Profiles and
 * the profile URL entered at setup matches none of them — ask which one.
 */
export default function GMBLocationPicker({ darkMode, onSelected }: GMBLocationPickerProps) {
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selecting, setSelecting] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await gmbApi.listLocations(getUserId()) as any;
      setLocations((response?.data || response)?.locations || []);
    } catch (err: any) {
      setError(err.message || 'Could not load your Business Profiles.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const select = async (location: LocationOption) => {
    setSelecting(location.location_name);
    try {
      await gmbApi.selectLocation(getUserId(), location.location_name);
      toast.success(`${location.title || 'Location'} selected. Syncing its data, this takes about 2 minutes.`);
      onSelected();
    } catch (err: any) {
      toast.error(err.message || 'Could not select that location.');
    } finally {
      setSelecting(null);
    }
  };

  const card = darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900';

  return (
    <div className={`mx-auto max-w-2xl rounded-xl border p-6 ${card}`}>
      <h2 className="text-lg font-semibold">Which Business Profile should we manage?</h2>
      <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Your Google account manages more than one Business Profile, and the profile URL you entered didn't match any of them.
        Pick the one for this brand.
      </p>

      {isLoading && <p className="mt-6 text-sm text-gray-500">Loading your Business Profiles...</p>}
      {error && (
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
          <button onClick={load} className="ml-3 font-semibold underline">Retry</button>
        </div>
      )}

      <div className="mt-5 space-y-2">
        {locations.map((location) => (
          <button
            key={location.location_name}
            type="button"
            disabled={!!selecting}
            onClick={() => select(location)}
            className={`flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors disabled:opacity-60 ${
              darkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div>
              <p className="text-sm font-medium">{location.title || location.location_name}</p>
              {location.address && <p className="text-xs text-gray-500">{location.address}</p>}
            </div>
            {selecting === location.location_name ? (
              <i className="ri-loader-4-line animate-spin text-teal-600"></i>
            ) : (
              <i className="ri-arrow-right-s-line text-gray-400"></i>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
